/**
 * Resolving a named report period to a concrete date range.
 *
 * Lifted verbatim from ImportTransactionsComponent, which had the only implementation
 * and now delegates here. The Transactions screen needs the same nine presets in its
 * date-range modal, and financial-year boundaries are subtle enough that a second
 * copy would drift.
 *
 * The named periods are the ReportPeriod enum's (src/app/core/enum/report-period.enum.ts),
 * which is the vocabulary the report-run sidenav already uses. Three extras --
 * Last 30 Days, Last 90 Days and All Dates -- exist only on the Transactions ledger,
 * where you are reading a statement rather than running a report, and are therefore
 * declared here rather than added to that enum.
 */

/** An organisation's fiscal calendar. Both fall back to Australia's 1 Jul – 30 Jun. */
export interface FinancialYearEnd {
  /** 1-12. Defaults to 6 (June). */
  month?: number | null;
  /** 1-31. Defaults to 30. */
  day?: number | null;
}

export interface DateRange {
  start: Date;
  /** Inclusive. Null only for All Dates, which has no lower or upper bound. */
  end: Date;
}

export const REPORT_PERIOD_THIS_MONTH = 'This Month';
export const REPORT_PERIOD_LAST_MONTH = 'Last Month';
export const REPORT_PERIOD_LAST_30_DAYS = 'Last 30 Days';
export const REPORT_PERIOD_LAST_90_DAYS = 'Last 90 Days';
export const REPORT_PERIOD_THIS_QUARTER = 'This Quarter';
export const REPORT_PERIOD_LAST_QUARTER = 'Last Quarter';
export const REPORT_PERIOD_THIS_FY = 'This Financial Year';
export const REPORT_PERIOD_LAST_FY = 'Last Financial Year';
export const REPORT_PERIOD_ALL_DATES = 'All Dates';
export const REPORT_PERIOD_CUSTOM = 'Custom Range';

/**
 * The presets the Transactions date-range modal lists down its left-hand side, in
 * order. Custom Range is absent on purpose: picking dates in the calendar is what
 * selects it, so offering it as a preset would be a button that does nothing.
 */
export const LEDGER_PERIOD_PRESETS: string[] = [
  REPORT_PERIOD_THIS_MONTH,
  REPORT_PERIOD_LAST_MONTH,
  REPORT_PERIOD_LAST_30_DAYS,
  REPORT_PERIOD_LAST_90_DAYS,
  REPORT_PERIOD_THIS_QUARTER,
  REPORT_PERIOD_LAST_QUARTER,
  REPORT_PERIOD_THIS_FY,
  REPORT_PERIOD_LAST_FY,
  REPORT_PERIOD_ALL_DATES
];

/**
 * The start of the financial year containing `reference`.
 * Exported because the import wizard needs it on its own.
 */
export function financialYearStart(reference: Date, fy?: FinancialYearEnd): Date {
  const endMonth = fy?.month ?? 6;   // 1-12 (default June)
  const endDay = fy?.day ?? 30;      // 1-31 (default 30)

  // FY end for reference's calendar year (month is 0-indexed inside Date).
  const fyEndThisYear = new Date(reference.getFullYear(), endMonth - 1, endDay);
  const isAfterEnd = reference.getTime() > fyEndThisYear.getTime();

  // The FY that *contains* reference ends at the next FY-end after it.
  const fyEnd = isAfterEnd
    ? new Date(reference.getFullYear() + 1, endMonth - 1, endDay)
    : fyEndThisYear;

  // Start = day after the previous FY-end.
  const start = new Date(fyEnd);
  start.setFullYear(start.getFullYear() - 1);
  start.setDate(start.getDate() + 1);
  return start;
}

export function financialYearEnd(fyStart: Date, fy?: FinancialYearEnd): Date {
  const endMonth = fy?.month ?? 6;
  const endDay = fy?.day ?? 30;

  // The first year-end falling on or after the start.
  //
  // The version this was lifted from did `fyStart.getFullYear() + 1`, which assumes
  // the financial year always straddles two calendar years. True of AU's 1 Jul - 30 Jun,
  // false of a 31 December year end: an organisation on a calendar financial year got
  // 1 Jan 2026 - 31 Dec 2027, a two-year range. report-period.spec.ts pins this.
  const candidate = new Date(fyStart.getFullYear(), endMonth - 1, endDay);

  return candidate >= fyStart
    ? candidate
    : new Date(fyStart.getFullYear() + 1, endMonth - 1, endDay);
}

/**
 * Resolves a named period to a range. `today` is injectable so this stays a pure
 * function and can be tested without freezing the clock.
 *
 * All Dates returns null -- the caller sends no date bounds at all rather than an
 * arbitrarily wide range, so the server reads the account's whole history.
 * An unknown name falls back to This Month, which is what the import wizard did.
 */
export function resolveReportPeriod(
  period: string,
  fy?: FinancialYearEnd,
  today: Date = new Date()
): DateRange | null {

  const now = today;

  switch (period) {
    case REPORT_PERIOD_ALL_DATES:
      return null;

    case REPORT_PERIOD_LAST_MONTH:
      return {
        start: new Date(now.getFullYear(), now.getMonth() - 1, 1),
        end: new Date(now.getFullYear(), now.getMonth(), 0)
      };

    case REPORT_PERIOD_LAST_30_DAYS:
      return { start: daysAgo(now, 29), end: startOfDay(now) };

    case REPORT_PERIOD_LAST_90_DAYS:
      return { start: daysAgo(now, 89), end: startOfDay(now) };

    case REPORT_PERIOD_THIS_QUARTER: {
      const q = Math.floor(now.getMonth() / 3);
      return {
        start: new Date(now.getFullYear(), q * 3, 1),
        end: new Date(now.getFullYear(), q * 3 + 3, 0)
      };
    }

    case REPORT_PERIOD_LAST_QUARTER: {
      const q = Math.floor(now.getMonth() / 3) - 1;
      const y = q < 0 ? now.getFullYear() - 1 : now.getFullYear();
      // Wraps Q1 back to Q4 of the previous year.
      const qn = ((q % 4) + 4) % 4;
      return {
        start: new Date(y, qn * 3, 1),
        end: new Date(y, qn * 3 + 3, 0)
      };
    }

    case REPORT_PERIOD_THIS_FY: {
      const start = financialYearStart(now, fy);
      return { start, end: financialYearEnd(start, fy) };
    }

    case REPORT_PERIOD_LAST_FY: {
      const thisFyStart = financialYearStart(now, fy);
      const lastFyStart = new Date(thisFyStart);
      lastFyStart.setFullYear(lastFyStart.getFullYear() - 1);
      return { start: lastFyStart, end: financialYearEnd(lastFyStart, fy) };
    }

    case REPORT_PERIOD_THIS_MONTH:
    default:
      return {
        start: new Date(now.getFullYear(), now.getMonth(), 1),
        end: new Date(now.getFullYear(), now.getMonth() + 1, 0)
      };
  }
}

function startOfDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function daysAgo(from: Date, days: number): Date {
  const result = startOfDay(from);
  result.setDate(result.getDate() - days);
  return result;
}
